/**
 * Case entry URLs and the main tab (Build / Canvas / Journal) the canvas page opens on.
 *
 * A case opens on its file page (`caseFileUrl`: FIR, notes, investigations, evidence,
 * notices, reports); the case list card and the dashboard case card use it. `caseCanvasUrl`
 * is for the explicit canvas actions only: the "Open Fusion Canvas" button on the file page,
 * the list card's canvas control, the post-create toast, and the Fusion sidebar's own entry
 * modal. The canvas page itself still lands on Canvas whatever the case holds (an empty
 * case shows the Canvas empty state that points to Build); only an explicit ?tab= names
 * another tab.
 */
export type MainTab = 'build' | 'canvas' | 'journal';

export const DEFAULT_MAIN_TAB: MainTab = 'canvas';

export function isMainTab(v: string | null | undefined): v is MainTab {
  return v === 'build' || v === 'canvas' || v === 'journal';
}

/** The tab named by ?tab=, else Canvas. */
export function resolveMainTab(tabParam: string | null | undefined): MainTab {
  return isMainTab(tabParam) ? tabParam : DEFAULT_MAIN_TAB;
}

/** URL of a case's canvas page; a tab is added to the query only when it is not the default. */
export function caseCanvasUrl(caseId: string, tab?: MainTab): string {
  const base = `/cases/${encodeURIComponent(caseId)}/canvas`;
  return tab && tab !== DEFAULT_MAIN_TAB ? `${base}?tab=${tab}` : base;
}

/** URL of a case's file page, the landing for a case opened from the list or the dashboard. */
export function caseFileUrl(caseId: string): string {
  return `/cases/${encodeURIComponent(caseId)}`;
}
